import { useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { MobileBottomBar } from "@/components/MobileBottomBar";
import { FinalCtaSection } from "@/components/FinalCtaSection";
import { EliteClientRoster } from "@/components/EliteClientRoster";

const GeoAustralia = () => {
  useEffect(() => {
    document.title = "CRM Automation Expert Australia | Marjohn Robillo";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Hire an elite CRM Automation Expert from the Philippines for your Australian agency. Perfect AEST/AWST timezone alignment and direct partnership.",
      );
    }

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.setAttribute("name", "keywords");
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute(
      "content",
      "CRM automation expert Australia, GoHighLevel expert AU, CRM automation Australia, RevOps specialist Australia, n8n developer Australia, technical VA Australia, Philippines automation expert AU, AEST timezone VA",
    );

    const ogTags: Record<string, string> = {
      "og:title": "CRM Automation Expert Australia | Marjohn Robillo",
      "og:description":
        "Hire an elite CRM Automation Expert from the Philippines for your Australian agency. Perfect AEST/AWST timezone alignment and direct partnership.",
      "og:type": "website",
      "og:url": "https://marjohnrobillo.me/ghl-automation-expert-australia",
      "og:image":
        "https://assets.cdn.filesafe.space/pLFrnXnIu7IgQ4WCaCfz/media/6ac3f6752c1c51a2ac7081a0.png",
      "twitter:card": "summary_large_image",
      "twitter:title": "CRM Automation Expert Australia | Marjohn Robillo",
      "twitter:description":
        "Hire an elite CRM Automation Expert for your Australian agency.",
      "twitter:image":
        "https://assets.cdn.filesafe.space/pLFrnXnIu7IgQ4WCaCfz/media/6ac3f6752c1c51a2ac7081a0.png",
    };
    Object.entries(ogTags).forEach(([name, content]) => {
      let element =
        document.querySelector(`meta[property="${name}"]`) ||
        document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(
          name.startsWith("og:") ? "property" : "name",
          name,
        );
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    });

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute(
      "href",
      "https://marjohnrobillo.me/ghl-automation-expert-australia",
    );
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "CRM Automation Expert for Australian Agencies",
    description:
      "Hire an elite CRM Automation Expert from the Philippines for your Australian agency. Perfect AEST/AWST timezone alignment and direct partnership.",
    url: "https://marjohnrobillo.me/ghl-automation-expert-australia",
    areaServed: ["AU"],
    address: { "@type": "PostalAddress", addressCountry: "PH" },
    priceRange: "$$",
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Can a Philippines-based CRM expert work Australian business hours?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. The Philippines shares an almost identical timezone with Australia, ensuring perfect real-time collaboration during AEST/AWST business hours.",
        },
      },
      {
        "@type": "Question",
        name: "How much does a CRM automation expert cost compared to an AU hire?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A senior CRM automation expert from the Philippines typically costs $1,500-$4,000/month compared to $8,000-$15,000 for equivalent Sydney or Melbourne-based talent.",
        },
      },
    ],
  };

  const features = [
    {
      title: "AEST/AWST Alignment",
      desc: "The Philippines shares an almost identical timezone with Australia, ensuring perfect real-time collaboration and instant support.",
    },
    {
      title: "Platform Mastery",
      desc: "Specialized in advanced CRM architecture, custom API integrations, and complex automation workflows for AU agencies.",
    },
    {
      title: "Direct ROI",
      desc: "Bypass the expensive local hiring costs in Sydney or Melbourne by partnering directly with elite offshore technical talent.",
    },
  ];

  return (
    <main
      className="min-h-screen relative pb-[72px] md:pb-0"
      style={{ background: "#FFFFFF" }}
    >
      <MobileBottomBar />
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-24 px-6 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 72% 30%, rgba(11,124,242,0.10) 0%, transparent 60%)",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-6 right-0 select-none hidden md:block"
        >
          <span
            className="font-display font-black leading-none"
            style={{
              fontSize: "clamp(140px, 20vw, 300px)",
              color: "rgba(17,143,247,0.07)",
              letterSpacing: "-0.07em",
            }}
          >
            AU
          </span>
        </div>

        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2.5 mb-8 justify-center">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ background: "#27DC5D" }}
            />
            <span
              className="font-sans text-xs font-semibold uppercase tracking-[0.22em]"
              style={{ color: "var(--primary)" }}
            >
              🇦🇺 Trusted by AU Marketing Agencies
            </span>
          </div>

          <h1
            className="font-display font-extrabold mb-7 leading-[0.95]"
            style={{
              fontSize: "clamp(40px, 5.6vw, 76px)",
              letterSpacing: "-0.045em",
              color: "#071E61",
            }}
          >
            Hire a CRM Automation Expert &amp; Specialist from the Philippines
            for{" "}
            <span style={{ color: "var(--primary)" }}>Australian Agencies</span>
          </h1>

          <p
            className="mx-auto mb-10"
            style={{
              fontSize: "clamp(17px, 1.5vw, 20px)",
              lineHeight: 1.55,
              color: "#536B89",
              maxWidth: "640px",
            }}
          >
            Get world-class CRM systems architecture at a competitive global
            rate. Perfect timezone overlap for seamless collaboration across
            Australia.
          </p>
        </div>
      </section>

      <EliteClientRoster />

      {/* Feature cards */}
      <section className="relative py-20 px-6 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div
              key={i}
              className="relative rounded-[24px] p-8 overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBFF 100%)",
                border: "1px solid rgba(8,46,103,0.07)",
                boxShadow: "0 14px 40px rgba(8,46,103,0.08)",
              }}
            >
              <div
                className="font-display font-black mb-5 select-none"
                style={{
                  fontSize: "56px",
                  lineHeight: 1,
                  color: "rgba(17,143,247,0.08)",
                  letterSpacing: "-0.04em",
                }}
              >
                0{i + 1}
              </div>
              <h3
                className="font-display font-bold mb-3"
                style={{
                  fontSize: "22px",
                  color: "#071E61",
                  letterSpacing: "-0.03em",
                }}
              >
                {f.title}
              </h3>
              <p
                style={{ fontSize: "15px", lineHeight: 1.55, color: "#536B89" }}
              >
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <FinalCtaSection />
      <Footer />
    </main>
  );
};

export default GeoAustralia;
